import { z } from "zod";
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const positiveVersion = z.number().int().positive();
const rowCount = z.number().int().nonnegative().safe();
export const candidateArtifactKindSchema = z.enum([
    "relationship_set",
    "process_projection",
]);
export const candidateArtifactGeneratorIdentitySchema = z.object({
    schemaVersion: z.literal(1),
    component: z.enum(["candidate_relationship_generator", "candidate_process_generator"]),
    name: z.string().trim().min(1).max(120),
    version: z.string().trim().min(1).max(80),
    implementationSha256: sha256,
}).strict();
export const candidateArtifactSetIdentitySchema = z.object({
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(200),
    artifactKind: candidateArtifactKindSchema,
    evidenceCutId: z.string().trim().min(1).max(80),
    evidenceCutManifestSha256: sha256,
    semanticClosureSha256: sha256,
    generator: candidateArtifactGeneratorIdentitySchema,
    artifactSchemaVersion: positiveVersion,
    payloadEpoch: positiveVersion,
}).strict();
export const candidateArtifactComponentSchema = z.object({
    name: z.enum([
        "canonical_entities",
        "entity_links",
        "relationship_observations",
        "process_instances",
        "process_state_observations",
        "process_transitions",
    ]),
    rowCount,
    locationManifestSha256: sha256,
}).strict();
export const candidateArtifactContentManifestSchema = z.object({
    schemaVersion: z.literal(1),
    artifactKind: candidateArtifactKindSchema,
    rowCount,
    canonicalContentSha256: sha256,
    components: z.array(candidateArtifactComponentSchema).min(1).max(6),
}).strict().superRefine((manifest, context) => {
    const names = manifest.components.map((component) => component.name);
    if (new Set(names).size !== names.length || names.join("\0") !== [...names].sort().join("\0")) {
        context.addIssue({ code: "custom", message: "Artifact components must be unique and sorted." });
    }
    const permitted = manifest.artifactKind === "relationship_set"
        ? new Set(["canonical_entities", "entity_links", "relationship_observations"])
        : new Set(["process_instances", "process_state_observations", "process_transitions"]);
    if (names.some((name) => !permitted.has(name))) {
        context.addIssue({ code: "custom", message: "Artifact component does not belong to its kind." });
    }
    const required = manifest.artifactKind === "relationship_set"
        ? "relationship_observations"
        : "process_state_observations";
    if (!names.includes(required)) {
        context.addIssue({ code: "custom", message: `Artifact is missing ${required}.` });
    }
    if (manifest.components.reduce((total, component) => total + component.rowCount, 0)
        !== manifest.rowCount) {
        context.addIssue({ code: "custom", message: "Artifact component counts do not sum to rowCount." });
    }
});
export const candidateArtifactPayloadLocationSchema = z.object({
    componentName: candidateArtifactComponentSchema.shape.name,
    locationOrdinal: z.number().int().nonnegative(),
    storageKind: z.enum(["legacy_run_table", "artifact_partition"]),
    storageVersion: positiveVersion,
    relationName: z.string().regex(/^twin_candidate_[a-z0-9_]+$/u),
    partitionName: z.string().regex(/^[a-z_][a-z0-9_]*$/u).nullable(),
    sourceRunId: z.string().trim().min(1).max(200),
    rowCount,
    payloadSha256: sha256,
}).strict().superRefine((location, context) => {
    if ((location.storageKind === "artifact_partition") !== (location.partitionName !== null)) {
        context.addIssue({
            code: "custom",
            message: "Only partition-backed locations name a physical partition.",
        });
    }
});
export const candidateArtifactRetirementEvidenceSchema = z.object({
    schemaVersion: z.literal(1),
    retentionClass: z.literal("payload_only"),
    preservedEnvelope: z.literal(true),
    activeBuildReferenceCount: z.literal(0),
    rollbackBuildReferenceCount: z.literal(0),
    unresolvedEvaluationReferenceCount: z.literal(0),
    archive: z.object({ locator: z.string().trim().min(1).max(500), sha256 }).strict().nullable(),
}).strict();
